import { GetPointResponse, PointOrderingOption } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { delay, isDefined } from '../helpers/utils';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { detectMobileViewport } from '../helpers/device';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('point-card-pagination')
export class PointCardPagination extends LitElement {

    @property()
    showAllPoints: boolean = false;

    @property({ type: Boolean })
    discoveryView: boolean = false; // when cards are on discovery page, we use a transparent background, only show one photo

    @property({ type: Boolean }) // if we are in standalone map view, we don't want to auto open
    standaloneMapView: boolean = false;

    @state()
    selectedPoint?: GetPointResponse;

    @state()
    selectedPoints: GetPointResponse[] = [];

    @state()
    highlightedPoint?: GetPointResponse;

    @state()
    allPoints: GetPointResponse[] = [];

    @state()
    sortedPoints: GetPointResponse[] = [];

    @state()
    pointsToRender: Set<GetPointResponse> = new Set();

    @state()
    previewMode: boolean = false;

    @state()
    mobilePreview: boolean = false;

    @state()
    multiSelectionEnabled: boolean = false;

    @state()
    autoOpenPointList: boolean = false;

    @state()
    useHorizontalLayout: boolean = detectMobileViewport(this.mobilePreview);

    @state()
    isInitialized: boolean = false;

    @state()
    isTransfering: boolean = false;

    @state()
    isContainingDrawerOpen: boolean = false;

    @state()
    isProgrammaticallyScrolling: boolean = false;

    @state()
    useInfiniteScroll: boolean = true;

    @state()
    showScrollHint: boolean = false;

    @state()
    hasPointsToRight: boolean = false;

    @state()
    hasPointsToLeft: boolean = false;

    @state()
    visibleCardId?: string;

    @state()
    currentCardIndex: number = 0;

    @state()
    isUserSwiping: boolean = false;

    @state()
    pauseScrollForLoading: boolean = false;

    private touchStartX: number = 0;
    private touchStartTime: number = 0;

    private readonly intersectionObserver?: IntersectionObserver;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    // on startup, if show on open is true, set points to render
    async firstUpdated() {
        // wait for pointCache to be initialized
        while (!pointCache.isReady) {
            await delay(100);
        }
        this.autoOpenPointList = topicData.topic?.show_point_list_on_open === true;

        if (this.standaloneMapView) {
            return;
        }

        this.isInitialized = true;

        // Set up touch handlers after points are ready and rendered
        setTimeout(() => {
            this.setupTouchHandlers();
        }, 500);

        if (this.autoOpenPointList) {
            if (this.highlightedPoint) {
                const index = pointCache.orderedPoints.indexOf(this.highlightedPoint);
                // index page number based on the highlighted point
                const page = Math.floor(index / 10);
                this.pointsToRender = new Set(pointCache.orderedPoints.slice((page - 1)*10, (page + 1) * 10));
                await this.scrollToPoint(this.highlightedPoint?._id.$oid);
            } else {
                this.pointsToRender = new Set(pointCache.orderedPoints.slice(0, 10));
            }
        } else {
            if (this.highlightedPoint) {
                this.pointsToRender = new Set([this.highlightedPoint]);
            } else {
                this.pointsToRender = new Set(pointCache.orderedPoints.slice(0, 10));
            }
        }

        // for handling updates from filters
        pointCache.onPointsFilteredOrFetched(() => {
            if (this.isTransfering) {
                return;
            }
            if (!this.multiSelectionEnabled) {
                this.pointsToRender = this.getPointsToRender();
            } else {
                // add new points to selected points
                const newPoints = pointCache.orderedPoints.filter((point) => !this.selectedPoints?.map((selectedPoint) => selectedPoint._id.$oid).includes(point._id.$oid));
                if (topicData.mapComponent?.selectedPoints) {
                    topicData.mapComponent.selectedPoints = [...this.selectedPoints, ...newPoints];
                }
                this.selectedPoints = [...this.selectedPoints, ...newPoints];
            }
        });
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (!this.isInitialized) {
            return;
        }
        if (changedProperties.has('highlightedPoint') || changedProperties.has('selectedPoints')) {
            if (isDefined(this.highlightedPoint)) {
                this.updatePointsToRender();
            }
        }

        if (changedProperties.has('mobilePreview')) {
            this.useHorizontalLayout = detectMobileViewport(this.mobilePreview);
        }

        if (detectMobileViewport(this.mobilePreview) && this.discoveryView) {
            this.useHorizontalLayout = false;
        }

        if (changedProperties.has('pointsToRender') && this.useHorizontalLayout) {
            // timeout to allow rendering to complete
            setTimeout(() => {
                this.showScrollHintOnMobile();
                this.updateScrollHints();
                // Ensure touch handlers are set up when points change
                this.setupTouchHandlers();
            }, 300);
        }
    }

    // Touch gesture handlers for mobile swipe experience

    private readonly handleTouchStart = (e: TouchEvent) => {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartTime = Date.now();
        this.isUserSwiping = true;
    };

    private readonly handleTouchMove = (e: TouchEvent) => {
        // eslint-disable-next-line no-useless-return
        if (!this.isUserSwiping) return;
        // Let native scroll handle it with snap points
    };

    private readonly handleTouchEnd = (e: TouchEvent) => {
        if (!this.isUserSwiping) return;
        
        const touchEndX = e.changedTouches[0].clientX;
        const touchDuration = Date.now() - this.touchStartTime;
        const touchDistance = Math.abs(touchEndX - this.touchStartX);
        const touchVelocity = touchDistance / touchDuration;
        
        // Detect fast swipe gestures for enhanced responsiveness
        if (touchVelocity > 0.5 && touchDistance > 50) {
            const swipeDirection = touchEndX > this.touchStartX ? 'right' : 'left';
            this.handleFastSwipe(swipeDirection);
        }
        
        this.isUserSwiping = false;
        
        // Update current card index and handle infinite scroll
        setTimeout(() => {
            this.updateCurrentCardIndex();
            this.checkScrollPosition();
        }, 100);
    };

    private readonly handleFastSwipe = (direction: 'left' | 'right') => {
        const scrollContainer = this.shadowRoot?.querySelector('.pagination--horizontal') as HTMLElement;
        if (!isDefined(scrollContainer)) return;
        
        const cardWidth = scrollContainer.clientWidth;
        const currentScroll = scrollContainer.scrollLeft;
        
        if (direction === 'left' && !this.isLastPointLoaded) {
            // Swipe to next card
            scrollContainer.scrollTo({
                left: currentScroll + cardWidth,
                behavior: 'smooth'
            });
        } else if (direction === 'right' && !this.isFirstPointLoaded) {
            // Swipe to previous card  
            scrollContainer.scrollTo({
                left: currentScroll - cardWidth,
                behavior: 'smooth'
            });
        }
    };

    private readonly handleScroll = () => {
        if (!this.isProgrammaticallyScrolling) {
            this.updateScrollHints();
            this.updateCurrentCardIndex();
            
            // Track visible card for map highlighting
            this.updateVisibleCard();
        }
    };

    private readonly updateCurrentCardIndex = () => {
        const scrollContainer = this.shadowRoot?.querySelector('.pagination--horizontal') as HTMLElement;
        if (!isDefined(scrollContainer)) return;
        
        const cardWidth = scrollContainer.clientWidth;
        const scrollLeft = scrollContainer.scrollLeft;
        const newIndex = Math.round(scrollLeft / cardWidth);
        
        if (newIndex !== this.currentCardIndex) {
            this.currentCardIndex = newIndex;
        }
    };

    private readonly updateVisibleCard = () => {
        const scrollContainer = this.shadowRoot?.querySelector('.pagination--horizontal') as HTMLElement;
        if (!isDefined(scrollContainer)) return;
        
        const cards = scrollContainer.querySelectorAll('.pagination__point');
        
        let closestCard: Element | null = null;
        let closestDistance = Infinity;
        
        cards.forEach((card) => {
            const cardRect = card.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const cardCenter = cardRect.left - containerRect.left + cardRect.width / 2;
            const distance = Math.abs(cardCenter - scrollContainer.clientWidth / 2);
            
            if (distance < closestDistance) {
                closestDistance = distance;
                closestCard = card;
            }
        });
        
        if (isDefined(closestCard) && closestCard !== null) {
            const pointId = (closestCard as any).id.replace('point-', '');
            if (this.visibleCardId !== pointId) {
                this.visibleCardId = pointId;
                void this.highlightCardOnMap(pointId);
            }
        }
    };

    private async highlightCardOnMap(pointId: string) {
        const point = [...this.pointsToRender].find(p => p._id.$oid === pointId);
        if (point && topicData.mapComponent) {
            // Highlight the point on the map
            await topicData.mapComponent.directlySetFocusedPoint(point._id.$oid, true);
        }
    }

    private setupTouchHandlers() {
        if (!this.isInitialized || !this.useHorizontalLayout) {
            return;
        }
        
        const element = this.shadowRoot?.querySelector('.pagination--horizontal');
        if (element && this.pointsToRender.size > 0) {
            // Enhanced touch handling for mobile swipe experience
            element.addEventListener('touchstart', this.handleTouchStart as EventListener, { passive: false });
            element.addEventListener('touchmove', this.handleTouchMove as EventListener, { passive: false });
            element.addEventListener('touchend', this.handleTouchEnd as EventListener, { passive: false });
            element.addEventListener('scroll', this.handleScroll, { passive: true });
        }
    }

    private readonly updateScrollHints = () => {
        if (!this.useHorizontalLayout) return;

        const scrollContainer = this.shadowRoot?.querySelector('.pagination--horizontal');
        if (!scrollContainer) return;

        const currentPoints = [...this.pointsToRender];
        const remainingScrollWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth - scrollContainer.scrollLeft;
        const scrolledLeft = scrollContainer.scrollLeft;

        // Update right hint
        const lastPointIndex = pointCache.orderedPoints.indexOf(currentPoints[currentPoints.length - 1]);
        this.hasPointsToRight = (lastPointIndex < pointCache.orderedPoints.length - 1) || (remainingScrollWidth > 50);

        // Update left hint
        const firstPointIndex = pointCache.orderedPoints.indexOf(currentPoints[0]);
        this.hasPointsToLeft = (firstPointIndex > 0) || (scrolledLeft > 50);
    };

    isHoveredCard(pointId: string) {
        return this.selectedPoint?._id.$oid === pointId;
    }

    updatePointsToRender(): void {
        // 1. If multi-selection is enabled, always render the selected points.
        if (this.multiSelectionEnabled && this.selectedPoints?.length > 0) {
            this.pointsToRender = new Set([...this.selectedPoints]);
            // (You might choose to leave infinite scroll on here if needed.)
            return;
        }

        // 2. If there's a highlighted point...
        if (this.highlightedPoint) {
            // Check if we should update pointsToRender (e.g. if the drawer is closed or the highlighted point isn’t already rendered)
            if (!this.pointsToRender.has(this.highlightedPoint) || !this.isContainingDrawerOpen) {

                // Case A: If the main sort order is by distance...
                if (topicData.topic?.point_sort_order[0] === PointOrderingOption.Distance) {
                    this.useInfiniteScroll = false;
                    // Get 20 closest points based on distance from the highlighted point,
                    // and ensure the highlighted point remains at the top.
                    const closestPoints = pointCache.getXNearestPoints(this.highlightedPoint.location.latLng, 20);
                    this.pointsToRender = new Set([this.highlightedPoint, ...closestPoints]);
                    // Turn off infinite scroll.
                }
                // Case B: If "show point list on open" is enabled...
                else if (topicData.topic?.show_point_list_on_open === true) {
                    // Get a slice of points based on sort index.
                    const slicePoints = this.getSliceAroundHighlightedPoint();
                    this.pointsToRender = new Set(slicePoints);
                    // Leave infinite scroll on.
                    this.useInfiniteScroll = true || detectMobileViewport(this.mobilePreview);
                }
                // Case C: Otherwise, just show the highlighted point.
                // Infinite scroll is enabled on mobile so swiping loads neighbors,
                // but checkScrollPosition guards against auto-loading with only 1 card.
                else {
                    this.pointsToRender = new Set([this.highlightedPoint]);
                    this.useInfiniteScroll = detectMobileViewport(this.mobilePreview);
                }
                }
            void this.scrollToPoint(this.highlightedPoint?._id.$oid);
            return;
        }

        // 3. If there's no highlighted point and show point list on open is enabled, fetch more points.
        if (topicData.topic?.show_point_list_on_open === true && !this.multiSelectionEnabled) {
            void this.fetchMorePoints();
        }
    }

    private getSliceAroundHighlightedPoint(): GetPointResponse[] {
        const index = pointCache.orderedPoints.findIndex(point => point._id.$oid === this.highlightedPoint?._id.$oid);
        // Fallback index: roughly the middle of the list plus an offset.
        const fallbackIndex = Math.floor(pointCache.orderedPoints.length / 2) + 4;
        const baseIndex = index !== -1 ? index : fallbackIndex;
        const start = Math.max(baseIndex - 5, 0);
        const end = baseIndex + 5;
        return pointCache.orderedPoints.slice(start, end);
    }

    private async onShowAddToMapModal(e: CustomEvent) {
       await topicData.mapComponent?.onShowAddToMapModal(this.selectedPoints);
    }

    private async sendSelectedPointsToRoute() {
        this.isTransfering = true;
        topicData.mapComponent?.clearAllFiltersClick();
        await delay(1000);
        await topicData.mapComponent?.onOpenCreateRoute(this.selectedPoints);
        // close the point list
        this.multiSelectionEnabled = false;
        this.selectedPoints = [];
        await (delay(1000));
        await topicData.mapComponent?.onDeselectAllAndClose();
        this.isTransfering = false;
    }

    private async scrollToPoint(pointId: string | undefined, smooth = true, hasDelay = true) {
        if (!pointId) return;
        await delay(hasDelay ? 500 : 0);
        const pointElement = this.shadowRoot?.querySelector(`#point-${pointId}`);
        if (pointElement) {
            const behavior = smooth ? 'smooth' : 'auto';
            this.isProgrammaticallyScrolling = true;
            if (detectMobileViewport(this.mobilePreview)) {
                pointElement.scrollIntoView({ behavior, inline: 'center' });
            } else {
                pointElement.scrollIntoView({ behavior, block: 'center' });
            }
            setTimeout(() => {
                this.isProgrammaticallyScrolling = false;
            }, 500);
        }
    }

    distance(lat1: number, lng1: number, lat2: number, lng2: number) {
        const p = 0.017453292519943295;    // Math.PI / 180
        const c = Math.cos;
        const a = 0.5 - c((lat2 - lat1) * p)/2 +
                c(lat1 * p) * c(lat2 * p) *
                (1 - c((lng2 - lng1) * p))/2;

        return 12742 * Math.asin(Math.sqrt(a)) * 1000; // 2 * R; R = 6371 km
    }

    isGateLimited(point: GetPointResponse) {
        if (topicData.topic?.is_gate_limited === true) {
            return point.is_gate_hidden;
        }
        else {
            return false;
        }
    }

    enableMultiSelection() {
        topicData.mapComponent?.onEnableMultiSelection();
    }

    async deselectAllAndClose() {
        await topicData.mapComponent?.onDeselectAllAndClose();
    }

    // similar to what we do in point drawer but mobile scrolling happens in a separate element for some reason
    connectedCallback() {
        super.connectedCallback();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeTouchHandlers();
    }

    private removeTouchHandlers() {
        const element = this.shadowRoot?.querySelector('.pagination--horizontal');
        if (element) {
            element.removeEventListener('touchstart', this.handleTouchStart as EventListener);
            element.removeEventListener('touchmove', this.handleTouchMove as EventListener);
            element.removeEventListener('touchend', this.handleTouchEnd as EventListener);
            element.removeEventListener('scroll', this.handleScroll);
        }
    }

    get isFirstPointLoaded() {
        return this.pointsToRender.has(pointCache.orderedPoints[0]);
    }

    get isLastPointLoaded() {
        return this.pointsToRender.has(pointCache.orderedPoints[pointCache.orderedPoints.length - 1]);
    }

    checkScrollPosition = () => {
        // if you are changing something here dont forget to do it in point-drawer too because this is only mobile
        // With only 1 card rendered, the container is immediately "at the edge" which would
        // trigger runaway point loading. Only check scroll position when there are 2+ cards.
        if (!this.isInitialized || this.isProgrammaticallyScrolling || !this.useInfiniteScroll || this.pauseScrollForLoading || this.pointsToRender.size <= 1) {
            return;
        }
        const scrollableArea = this.shadowRoot?.querySelector('.pagination--horizontal');
        if (!scrollableArea) return;

        const { scrollLeft, clientWidth } = scrollableArea;
        const endOfScrollableArea = scrollLeft + clientWidth;
        const startOfScrollableArea = scrollLeft;

        // Check if the user has scrolled within 100px of the end
        if (endOfScrollableArea >= scrollableArea.scrollWidth - 10 && topicData.mapComponent?.multiSelectionEnabled !== true) {
            // if first point is already in the points to render, do nothing
            if (this.isLastPointLoaded) {
                this.updateScrollHints();
                return;
            }
            void this.fetchMorePoints('next');
        } else if (startOfScrollableArea <= 10 && topicData.mapComponent?.multiSelectionEnabled !== true) {
            // if last point is already in the points to render, do nothing
            if (this.isFirstPointLoaded) {
                this.updateScrollHints();
                return;
            }
            void this.fetchMorePoints('previous');
        }
        this.updateScrollHints();
    };

    private showScrollHintOnMobile(): void {
        if (!detectMobileViewport(this.mobilePreview) || !this.useHorizontalLayout) {
            return;
        }

        // if we showed it in the last 30 days, don't show again
        const lastShown = localStorage.getItem('pointCardScrollHintShown');
        if (lastShown) {
            const lastShownTime = parseInt(lastShown, 10);
            const now = Date.now();
            const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
            if (now - lastShownTime < thirtyDaysInMs) {
                return;
            }
        }

        // Only show if there are multiple points to scroll through
        if (this.pointsToRender.size < 2) {
            return;
        }

        // Show hint briefly
        this.showScrollHint = true;

        // Hide after 2 seconds
        setTimeout(() => {
            this.showScrollHint = false;

            // save timestamp to local storage to prevent showing again for 30 days
            localStorage.setItem('pointCardScrollHintShown', Date.now().toString());
        }, 2000);

    }

    render() {
        const hasPointsToRender = this.pointsToRender.size > 0;
        return html`
            <div class="point-card-pagination-wrapper">
                ${this.multiSelectionEnabled ? html`
                    <div class="multi-select-control">
                        <div class="select-actions">
                            <span><b>${this.selectedPoints?.length}</b> points selected</span>
                            <div class="deselect-all-button" @click="${this.deselectAllAndClose}">Deselect All and Close</div>
                        </div>
                        <div class="next-actions">
                            ${topicData.topic?.disable_add_to_map !== true || (topicData.isWriter && accountInfo.isAuthenticated && !this.previewMode) ? html`
                                <div class="next-action-button" @click="${this.onShowAddToMapModal}">${topicData.isWriter ? 'Add to Another Map' : 'Add to Your Own Map'}</div>
                            ` : ''}
                            ${topicData.topic?.hide_get_route !== true || (topicData.isWriter && !this.previewMode) ? html`
                                <div class="next-action-button" @click="${this.sendSelectedPointsToRoute}">Get Route</div>
                            ` : ''}
                        </div>
                    </div>
                ` : ''}

                ${this.showScrollHint ? html`
                    <div class="scroll-hint">
                        <span>← Scroll to see more points →</span>
                        <br/><br/>
                        <span> 👆 Tap on a place to see more details </span>
                    </div>
                ` : ''}

                <div class="pagination ${this.useHorizontalLayout ? 'pagination--horizontal' : 'pagination--vertical'} ${!this.showAllPoints ? 'pagination--not-all-points': ''}">
                ${[...this.pointsToRender].map((point) => {
                    if (this.isGateLimited(point)) {
                        return '';
                    }
                    const isSelected = this.selectedPoints?.map((selectedPoint) => selectedPoint._id.$oid).includes(point._id.$oid);
                    return html`
                        <point-card-wrapper
                            class="pagination__point"
                            id="point-${point._id.$oid}"
                            .discoveryView=${this.discoveryView}
                            .isSelected=${isSelected}
                            .point=${point}
                            .previewMode=${this.previewMode}
                            .multiSelectionMode=${this.multiSelectionEnabled}
                            .mobilePreview=${this.mobilePreview}
                            .expandAll=${this.showAllPoints}
                            .hasScrollRight=${this.hasPointsToRight}
                            .hasScrollLeft=${this.hasPointsToLeft}
                        >
                        </point-card-wrapper>
                        ${isSelected && !this.multiSelectionEnabled ? html`
                            ${!detectMobileViewport(this.mobilePreview) && hasPointsToRender ? this.renderMoreButtonsGroup() : ''}
                        ` : ''}
                        `;
                })}
                </div>
            </div>
        `;
    }

    renderMoreButtonsGroup() {
        const only_one_rendered = this.pointsToRender.size === 1;
        return html`
        <div class="more-buttons-group">
            ${!this.multiSelectionEnabled ? html`
                ${only_one_rendered && topicData.topic?.is_gate_limited !== true && !detectMobileViewport(this.mobilePreview) ?
                    html `
                        <div @click="${async () => await this.fetchMorePoints('next', true)}" class="pagination__more-points">
                            <mwc-icon>list</mwc-icon>
                            <translated-span>View More</translated-span>
                        </div>`
                    :
                    only_one_rendered && topicData.topic?.is_gate_limited === true ?
                    html `
                        <mwc-icon>unlock</mwc-icon>
                        <span>Access The Map To See More</span>
                    `:''
                }
                ${topicData.topic?.is_gate_limited !== true && topicData.topic?.hide_multi_select !== true ? html`
                    <div @click=${this.enableMultiSelection} class="pagination__more-points">
                        <img src="/images/multi-select.svg" alt="multi-select" />
                        <translated-span>Select More</translated-span>
                    </div>
                ` : ''}
            ` : ''}
        </div>
        `;
    }

    getPointsToRender() {
        if (this.highlightedPoint) {
            const location_in_array = pointCache.orderedPoints.find((point) => point._id.$oid === this.highlightedPoint?._id.$oid);
            let index = (pointCache.orderedPoints.length)/2 + 4;
            if (location_in_array !== undefined) {
                index = pointCache.orderedPoints.indexOf(location_in_array);
            }
            const points_to_render = pointCache.orderedPoints.slice(Math.max(index - 5,0), Math.min(index + 5, pointCache.orderedPoints.length));
            return new Set(points_to_render);
        } else {
            return new Set(pointCache.orderedPoints.slice(0, 10));
        }
    }

    async fetchMorePoints(direction: 'next' | 'previous' = 'next', via_view_more_click = false) {
        const currentPoints = [...this.pointsToRender];
        const num_points = this.pointsToRender.size;
        const last_point_index = pointCache.orderedPoints.indexOf(currentPoints[num_points - 1]);
        const first_point_index = pointCache.orderedPoints.indexOf(currentPoints[0]);
        let new_points: GetPointResponse[] = [];

        if (direction === 'next') {
            new_points = pointCache.orderedPoints.slice(last_point_index + 1, last_point_index + 11);
            this.pointsToRender = new Set([...currentPoints, ...new_points]);
        } else {
            const first_point = pointCache.orderedPoints[first_point_index];
            new_points = pointCache.orderedPoints.slice(Math.max(first_point_index - 10, 0), first_point_index);
            this.pointsToRender = new Set([...new_points, ...currentPoints]);
            // scroll to the first point so the user doesn't notice the change
            const point_to_scroll_to = detectMobileViewport(this.mobilePreview) ? first_point : pointCache.orderedPoints[first_point_index + 2];
            await this.scrollToPoint(point_to_scroll_to._id.$oid, false, false);
        }

        if (via_view_more_click) {
            await this.scrollToPoint(this.highlightedPoint?._id.$oid, true, false);
            if (topicData.topic?.point_sort_order[0] === PointOrderingOption.Distance) {
                this.useInfiniteScroll = false;
            } else {
                this.useInfiniteScroll = true;
            }
        }
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                overflow-y: auto;
            }

            .point-card-pagination-wrapper {
                display: flex;
                flex-direction: column;
                overflow-y: auto;
                overflow-x: hidden;
                scrollbar-width: none; /* Firefox */
                -ms-overflow-style: none;  /* IE and Edge */
            }

            .point-card-pagination-wrapper::-webkit-scrollbar {
                display: none;
            }

            .pagination--vertical.pagination--not-all-points {
                padding: 20px;
                padding-right: 0;
            }

            .pagination--vertical .pagination__point {
                margin-bottom: 8px;
                display: block;
                width: 100%;
            }

            .pagination--horizontal {
                display: flex;
                align-items: end;
                overflow-x: auto;
                scroll-behavior: smooth;
                /* Snap scrolling for mobile swipe effect */
                scroll-snap-type: x mandatory;
                -webkit-overflow-scrolling: touch;
                -webkit-scroll-snap-type: x mandatory;
                scrollbar-width: none;
                -ms-overflow-style: none;
            }

            .pagination--horizontal::-webkit-scrollbar {
                display: none;
            }

            .pagination--horizontal .pagination__point {
                /* Each card takes full width and snaps to center on mobile */
                flex: 0 0 calc(100vw - 40px);
                min-width: calc(100vw - 40px);
                margin-right: 12px;
                height: 100%;
                display: block;
                margin-bottom: 4px;
                scroll-snap-align: center;
                scroll-snap-stop: always;
            }

            .pagination--horizontal .pagination__point:first-of-type {
                margin-left: 20px;
            }

            .pagination--horizontal .pagination__point:last-of-type {
                margin-right: 20px;
            }

            .pagination--horizontal .pagination__more-points {
                margin-bottom: 115px;
                flex-shrink: 0;
            }

            .pagination__more-points {
                background: white;
                border-radius: 4px;
                padding: 3px 8px;
                display: flex;
                flex-direction: row;
                align-items: center;
                cursor: pointer;
                gap: 0.5rem;
                user-select: none;
            }

            .pagination__more-points mwc-icon {
                --mdc-icon-size: 20px;
            }

            .pagination__more-points img {
                width: 20px;
                height: 20px;
            }

            .pagination__more-points span {
                margin-left: 0.5rem;
            }

            .more-buttons-group {
                display: flex;
                gap: 0.5rem;
                justify-content: flex-end;
                margin-bottom: 0.5rem;
            }

            .more-buttons-group--mobile {
                justify-content: center;
                position: fixed;
                top: 50px;
                width: 100%;
            }

            .multi-select-control {
                margin-left: 1.25rem;
            }

            .select-actions {
                display: flex;
                gap: 0.5rem;
                align-items: center;
                justify-content: space-between;
                padding: 0.5rem 1rem;
                background-color: var(--theme-white-color);
                font-size: 16px;
            }

            .deselect-all-button {
                cursor: pointer;
                border: 1px solid var(--theme-border-color);
                border-radius: 2rem;
                padding: 0.5rem 1rem;
                font-size: 14px;
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px -2px rgba(31, 27, 45, 0.08);
            }

            .next-actions {
                display: flex;
                gap: 0.5rem;
                padding: 0.5rem 1rem;
                background-color: var(--theme-light-scrollbar-color);
                border-radius: 0 0 0.5rem 0.5rem;
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px -2px rgba(31, 27, 45, 0.08);
            }

            .next-action-button {
                cursor: pointer;
                border: 1px solid var(--theme-border-color);
                background-color: var(--theme-white-color);
                padding: 0.5rem 1rem;
                font-size: 14px;
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px -2px rgba(31, 27, 45, 0.08);
            }

            .scroll-hint {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 0.5rem 1rem;
                border-radius: 20px;
                font-size: 14px;
                z-index: 1000;
                animation: fadeInOut 2s ease-in-out;
                pointer-events: none;
            }

            @keyframes fadeInOut {
                0%, 100% { opacity: 0; }
                20%, 80% { opacity: 1; }
            }

            @media only screen and (max-width: 550px) {
                .multi-select-control {
                    margin-left: 0;
                    margin-bottom: 0;
                }

                .select-actions {
                    padding: 0.5rem;
                }

                .next-actions {
                    padding: 0.5rem;
                }

                .point-card-pagination-wrapper {
                    flex-direction: column-reverse;
                }

                .pagination--horizontal .pagination__point:first-of-type {
                    margin-left: 20px;
                }

                .pagination--horizontal {
                    overflow: auto;
                    margin-bottom: 0;
                }

                .pagination--horizontal .pagination__more-points {
                    margin-bottom: 0;
                    border-radius: 8px;
                }
            }
        `;
    }
}
